import React, { useRef, useState, useEffect, useCallback } from "react";
import { fetchLogo } from "../../api";

function ChatTongole() {
  // Determine if chat toggle should be hidden (xbet50 origin)
  const hideChat = (() => {
    try { return window.location.hostname.toLowerCase().includes('xbet50'); }
    catch (_) { return false; }
  })();

  const chatRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [logo, setLogo] = useState("");
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [dragDistance, setDragDistance] = useState(0);
  const [show, setShow] = useState(null);
  const dragRef = useRef({ dragging: false, dragDistance: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef({
    x: typeof window !== 'undefined' ? window.innerWidth - 70 : 300,
    y: typeof window !== 'undefined' ? window.innerHeight - 150 : 500,
  });

  const [position, setPosition] = useState(positionRef.current);

  // Fetch logo using centralized API (cached)
  useEffect(() => {
    let cancelled = false;
    const loadLogo = async () => {
      try {
        const data = await fetchLogo();
        if (cancelled) return;
        if (data?.[0]?.mobile_logo) {
          setLogo(`${process.env.REACT_APP_API_URL}/storage/${data[0].mobile_logo}`);
        }
      } catch (_) { /* non-critical */ }
    };
    loadLogo();
    return () => { cancelled = true; };
  }, []);

  // Initialize chat SDK only if needed
  useEffect(() => {
    if (window.location.origin === "https://www.chat.com") {
      window.__lc = window.__lc || {};
      window.__lc.license = 19116631;
      (function (n, t, c) {
        function i(n) { return e._h ? e._h.apply(null, n) : e._q.push(n); }
        var e = {
          _q: [], _h: null, _v: "2.0",
          on: function () { i(["on", c.call(arguments)]); },
          once: function () { i(["once", c.call(arguments)]); },
          off: function () { i(["off", c.call(arguments)]); },
          get: function () {
            if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
            return i(["get", c.call(arguments)]);
          },
          call: function () { i(["call", c.call(arguments)]); },
          init: function () {
            var n = t.createElement("script");
            n.async = !0; n.type = "text/javascript";
            n.src = "https://cdn.livechatinc.com/tracking.js";
            t.head.appendChild(n);
          },
        };
        !n.__lc.asyncInit && e.init();
        n.LiveChatWidget = n.LiveChatWidget || e;
      })(window, document, [].slice);
    }
  }, []);

  // Read chat config from localStorage (once on mount)
  useEffect(() => {
    const whatsapp = localStorage.getItem("whatsapp");
    const liveChat = localStorage.getItem("liveChat");
    if (liveChat && liveChat !== 'null') {
      setShow('live_chat');
    } else {
      setShow('base_chat');
    }
    setWhatsappLink((whatsapp && whatsapp !== 'null') ? whatsapp : null);
  }, []); // Run once on mount, not on every show change

  // Drag handlers using refs to avoid stale closure issues
  const handleStart = useCallback((e) => {
    dragRef.current.dragging = true;
    dragRef.current.dragDistance = 0;
    setDragging(true);
    setDragDistance(0);

    const event = e.type === "touchstart" ? e.touches[0] : e;
    const rect = chatRef.current?.getBoundingClientRect();
    if (!rect) return;

    offsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleEnd);
  }, []);

  const handleMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const event = e.type === "touchmove" ? e.touches[0] : e;
    e.preventDefault();

    const newX = event.clientX - offsetRef.current.x;
    const newY = event.clientY - offsetRef.current.y;

    const distance = Math.sqrt(
      Math.pow(newX - positionRef.current.x, 2) + Math.pow(newY - positionRef.current.y, 2)
    );
    dragRef.current.dragDistance = distance;
    setDragDistance(distance);

    positionRef.current = { x: newX, y: newY };
    setPosition({ x: newX, y: newY });
  }, []);

  const handleEnd = useCallback((e) => {
    // Prevent link click if dragged more than 10px
    if (dragRef.current.dragDistance > 10) {
      e?.preventDefault?.();
      e?.stopPropagation?.();
    }

    dragRef.current.dragging = false;
    setDragging(false); // FIXED: was setDragging(true)

    // Cleanup global listeners
    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseup", handleEnd);
    document.removeEventListener("touchmove", handleMove);
    document.removeEventListener("touchend", handleEnd);
  }, [handleMove]); // include handleMove in deps since it's stable (useCallback with [])

  // Listen for custom 'open_chat' event
  useEffect(() => {
    const handler = () => handleChatClick();
    window.addEventListener('open_chat', handler);
    return () => window.removeEventListener('open_chat', handler);
  }, []); // stable, no deps needed

  const handleChatClick = useCallback((e) => {
    if (e) e.preventDefault();

    let waLink = whatsappLink;
    // Re-read from localStorage synchronously if not set
    if (!waLink || waLink === 'null' || waLink.length === 0) {
      const stored = localStorage.getItem("whatsapp");
      if (stored && stored !== 'null') waLink = stored;
    }

    const remoteId = localStorage.getItem("remote_id");
    const username = localStorage.getItem("username");

    if (window.Intercom) {
      window.Intercom("show");
    } else if (waLink && waLink !== 'null' && waLink.length > 0) {
      window.open(waLink, "_blank");
    } else {
      if (window?.anw2?.open) window.anw2.open();
      if (window.LiveChatWidget) {
        window.LiveChatWidget.once("ready", () => {
          if (remoteId && username) {
            window.LiveChatWidget.call("set_customer_data", { name: username, external_id: remoteId });
          }
          window.LiveChatWidget.call("maximize");
        });
      }
      if (window.Tawk_API) window.Tawk_API.maximize();
    }
  }, [whatsappLink]);

  if (hideChat) return null;
  if (show !== null && show !== 'live_chat' && !whatsappLink) return null;

  return (
    <>
      <style>{`
        [id^="anw2-launcher-container-"] [id^="anw2-launcher-"] {
          display: none !important;
        }
      `}</style>

      <a href="#chat" onClick={handleChatClick}>
        <div
          ref={chatRef}
          className="chat-tongole"
          style={{
            position: "fixed",
            left: `${position.x}px`,
            top: `${position.y}px`,
            cursor: dragging ? "grabbing" : "grab",
            zIndex: 1000,
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            overflow: "hidden",
            backgroundColor: "rgb(255 138 20)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          }}
          onMouseDown={handleStart}
          onTouchStart={handleStart}
        >
          <img src={logo} alt="Chat Icon" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
        </div>
      </a>
    </>
  );
}

export default ChatTongole;
